import { Component, Output, EventEmitter, OnInit } from '@angular/core';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { ManagerReasonService } from '../../services/manager-reason.service';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IPartner } from '../../data/partner';
import { ToastService } from '../../../../shared/services/toast.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PartnerService } from '../../services/partner.service';
import { CompanyService } from '../../../companies/services/company.service';
import { ICompany } from '../../../companies/models/company.interface';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { ResponseDefault } from '../../../../shared/data/response-default.interface';
import { SessionService } from '../../../../shared/services/session.service';
import { canPickCompany } from '../../../../shared/utilities/company-scope';

@Component({
  selector: 'app-edit-reason',
  templateUrl: './edit-reason.component.html',
  styleUrls: ['./edit-reason.component.scss']
})
export class EditReasonComponent implements OnInit {
  ERoleAdminString = ERoleAdminString;
  formOpen = false;
  reasonId = '';
  formReason = new UntypedFormGroup({
    description: new UntypedFormControl('', [Validators.required]),
    partnerId: new UntypedFormControl({ value: null, disabled: true }),
    companyIds: new UntypedFormControl([], [Validators.required])
  });

  partners: IPartner[] = [];
  companies: ICompany[] = [];
  // Usuário de empresa não troca os vínculos do motivo (mantém os carregados); interno e parceiro sem empresa escolhem.
  podeEscolherEmpresa = false;
  @Output() reloadReasons = new EventEmitter();

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly managerReasonService: ManagerReasonService,
    private readonly partnerService: PartnerService,
    private readonly companyService: CompanyService,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly sessionService: SessionService
  ) {}

  ngOnInit(): void {
    this.podeEscolherEmpresa = canPickCompany(this.sessionService.getSession());
    this.reasonId = this.activatedRoute.snapshot.paramMap.get('id')!;

    if (this.reasonId) {
      this.partnerService.listActive().subscribe((response) => {
        this.partners = response.data;
      });

      forkJoin({
        reason: this.managerReasonService.getReasonById(this.reasonId),
        companies: this.companyService.listarEmpresasAtivas(),
        // Falha no vínculo de empresas não pode travar o resto do formulário
        // (endpoint novo: durante a janela de deploy o backend pode responder 404).
        reasonCompanies: this.managerReasonService
          .getReasonCompanies(this.reasonId)
          .pipe(catchError(() => of<ResponseDefault<string[]>>({ isSucceed: false, data: [] })))
      })
        .pipe(
          catchError(() => {
            this.toastService.toast('Erro ao carregar o motivo de atendimento.');
            return of(null);
          })
        )
        .subscribe((result) => {
          if (!result) {
            return;
          }

          const { reason, companies, reasonCompanies } = result;

          // As empresas selecionáveis são as do parceiro do motivo: vincular a outro
          // parceiro é rejeitado pelo AddCompaniesToReason.
          this.companies = (companies.data ?? []).filter(
            (company) => !reason.data?.partnerId || company.partnerId === reason.data.partnerId
          );

          if (reasonCompanies.isSucceed) {
            this.formReason.get('companyIds')?.setValue(reasonCompanies.data ?? []);
          }

          if (reason.isSucceed) {
            this.formReason.get('description')?.setValue(reason.data.description);
            this.formReason.get('partnerId')?.setValue(reason.data.partnerId);
          }
        });
    }
  }

  editReason(): void {
    if (this.formReason.invalid) {
      this.formReason.markAllAsTouched();
      return;
    }

    const companyIds = this.formReason.get('companyIds')?.value ?? [];

    // O resultado carrega a etapa que o produziu: sem isso, um vínculo recusado
    // (HTTP 200 com isSucceed: false) seria reportado como falha do próprio motivo.
    this.managerReasonService
      .updateReason(this.reasonId, this.formReason.getRawValue())
      .pipe(
        switchMap((response) => {
          if (!response.isSucceed) {
            return of({ step: 'reason' as const, ok: false });
          }

          return this.managerReasonService.addCompaniesToReason({ reasonId: this.reasonId, companyIds }).pipe(
            map((link) => ({ step: 'companies' as const, ok: link.isSucceed })),
            catchError(() => of({ step: 'companies' as const, ok: false }))
          );
        })
      )
      .subscribe({
        next: (result) => {
          if (result.step === 'reason') {
            this.toastService.toast('Erro ao atualizar motivo de atendimento.');
            return;
          }

          // O PUT do motivo já rodou: navegar mesmo com o vínculo pendente evita
          // que o usuário salve o motivo de novo tentando "consertar" o erro.
          this.toastService.toast(
            result.ok ? 'Motivo atualizado com sucesso.' : 'Motivo salvo, mas não foi possível vincular as empresas.'
          );
          this.router.navigateByUrl('/dash/motivos-atendimento');
        },
        error: () => this.toastService.toast('Erro ao atualizar motivo de atendimento.')
      });
  }
}
